import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearOwnerDb } from "./db";
import { getAuthToken, syncApiUrl, setAuthToken } from "@cliqsuite/db";

export interface OwnerUser {
  id: string;
  business_id: string;
  email: string;
  name: string;
  role: string;
  // Empty = every store (the owner, and any manager created before this
  // field existed). A non-empty array restricts which stores show up in
  // the store switcher — see lib/storeScope.tsx.
  store_ids: string[];
  // Null = never proved ownership of this email — soft gate only, see
  // components/shell/VerificationBanner.tsx; never blocks login.
  email_verified_at: string | null;
}

export interface OwnerBusiness {
  id: string;
  name: string;
  slug: string;
  subscription_status: string;
}

interface AuthState {
  user: OwnerUser | null;
  loading: boolean;
  error: string;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (
    businessName: string,
    storeName: string,
    email: string,
    password: string,
    pin: string,
    name: string,
    businessType: string,
    address: string,
    currencyCode: string,
  ) => Promise<boolean>;
  logout: () => void;
  // Shared by accept-invite/reset-password (components/shell/AcceptInvitePage.tsx,
  // ResetPasswordPage.tsx) — both endpoints return a fresh { user, token }
  // just like login/signup, and should log the person straight in.
  setSession: (user: OwnerUser, token: string) => void;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = "cliqsuite_owner_session_v1";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OwnerUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as OwnerUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => user !== null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    void fetch(syncApiUrl("/auth/session"), { headers: { authorization: `Bearer ${token}` } })
      .then((response) => {
        if (response.status === 401 || response.status === 403) return null;
        if (!response.ok) return undefined;
        return response.json() as Promise<{ scope?: string; sub?: string; businessId?: string }>;
      })
      .then((session) => {
        if (cancelled) return;
        if (session === undefined) return;
        if (
          session &&
          session.scope === "business" &&
          session.sub === user.id &&
          session.businessId === user.business_id
        ) return;
        setAuthToken(null);
        setUser(null);
      })
      .catch(() => {
        // Network failure is compatible with offline use; it must not turn
        // into a destructive logout. The saved token is checked again by
        // the server as soon as synchronization reconnects.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Validate restored state before any scoped collection or PGlite sync
    // mounts. A fresh login has already been checked by the server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      async login(email: string, password: string) {
        setLoading(true);
        setError("");
        try {
          const res = await fetch(syncApiUrl("/auth/owner-login"), {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ email, password }),
          });
          if (!res.ok) {
            setError("Incorrect email or password.");
            return false;
          }
          const data = (await res.json()) as { ok: boolean; user: OwnerUser; token: string };
          setAuthToken(data.token);
          setUser(data.user);
          return true;
        } catch {
          setError("Couldn't reach the server. Is sync-api running?");
          return false;
        } finally {
          setLoading(false);
        }
      },
      async signup(businessName: string, storeName: string, email: string, password: string, pin: string, name: string, businessType: string, address: string, currencyCode: string) {
        setLoading(true);
        setError("");
        try {
          const res = await fetch(syncApiUrl("/auth/owner-signup"), {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ businessName, storeName, email, password, pin, name, businessType, address: address.trim() || undefined, currencyCode }),
          });
          if (res.status === 409) {
            const body = (await res.json().catch(() => null)) as { error?: string } | null;
            setError(body?.error === "email_already_registered"
              ? "That email already has a CliqSuite account. Log in or reset its password instead."
              : "That account already exists. Try logging in instead.");
            return false;
          }
          if (!res.ok) {
            setError("Couldn't create your account. Check your details and try again.");
            return false;
          }
          const data = (await res.json()) as { ok: boolean; user: OwnerUser; token: string };
          setAuthToken(data.token);
          setUser(data.user);
          return true;
        } catch {
          setError("Couldn't reach the server. Is sync-api running?");
          return false;
        } finally {
          setLoading(false);
        }
      },
      setSession(nextUser: OwnerUser, token: string) {
        setAuthToken(token);
        setUser(nextUser);
      },
      logout() {
        setAuthToken(null);
        setUser(null);
        // Wipe the local database, then hard-navigate so the module-level collections drop
        // their in-memory rows too; whoever signs in next syncs from scratch.
        void clearOwnerDb().finally(() => {
          window.location.href = "/";
        });
      },
    }),
    [user, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
